<script setup lang="ts">
import { storeToRefs } from 'pinia'
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuRoot,
  DropdownMenuTrigger,
} from 'reka-ui'
import { computed } from 'vue'

import { api } from '@/api'

import { router } from '../router'
import { useSessionStore } from '../session-store'
import BrandMark from './BrandMark.vue'
import { initials } from './initials'

const session = useSessionStore()
const { me } = storeToRefs(session)

// The course name from GET /me; the contract allows null, and then the header shows "-".
const courseLabel = computed(() => {
  if (!me.value) return ''
  return me.value.courseName ?? '-'
})
const userName = computed(() => (me.value ? (me.value.employeeName ?? '-') : '-'))
const userInitials = computed(() => initials(me.value?.employeeName ?? null))

// Shows the signed-out page at once and ends the session on the server in the background. The
// page stays loaded, so the call completes; if it fails, a 401 means the session is already
// gone and the cookie expires on its own.
function signOut() {
  void api.DELETE('/me').catch(() => {
    // Nothing to recover: the user has already left.
  })
  session.signOut()
  void router.push({ name: 'signed-out' })
}
</script>

<template>
  <header
    class="flex h-[50px] shrink-0 items-center justify-between gap-4 bg-bar px-5 text-on-bar shadow-header"
    data-testid="app-header"
  >
    <div class="flex min-w-0 items-center gap-3">
      <BrandMark />
      <p
        class="truncate border-l border-on-bar pl-3 text-body-xs font-medium text-on-bar"
        data-testid="course-label"
      >
        {{ courseLabel }}
      </p>
    </div>
    <DropdownMenuRoot>
      <DropdownMenuTrigger
        class="flex shrink-0 cursor-pointer items-center gap-2.5 rounded-md p-1 text-left text-on-bar"
        data-testid="app-header-user-menu"
      >
        <span
          v-if="userInitials"
          aria-hidden="true"
          data-testid="app-header-user-initials-label"
          class="flex size-[30px] items-center justify-center rounded-full border border-on-bar bg-surface text-body-xs font-semibold text-bar"
        >
          {{ userInitials }}
        </span>
        <div class="flex flex-col">
          <p
            class="text-body-sm leading-[1.15] font-semibold text-on-bar"
            data-testid="app-header-user-name-label"
          >
            {{ userName }}
          </p>
          <p
            v-if="me?.externalRole"
            class="text-body-xs leading-[1.15] font-medium text-on-bar"
            data-testid="app-header-user-role-label"
          >
            {{ me.externalRole }}
          </p>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuPortal>
        <DropdownMenuContent
          align="end"
          :side-offset="6"
          class="z-50 min-w-40 rounded-md border border-line bg-surface py-1 shadow-md"
        >
          <DropdownMenuItem
            data-testid="app-header-sign-out"
            class="flex cursor-pointer flex-col px-4 py-2 outline-none data-[highlighted]:bg-canvas"
            @select="signOut"
          >
            <span class="text-body-sm font-semibold text-ink">Logout</span>
            <span class="text-body-xs text-muted">Sign out of revUP</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenuRoot>
  </header>
</template>
